/** 세금계산서 — 내 신청 내역 · 새로 신청 · 취소. 상태는 회계팀이 발행대장에 적은 값(발행자·입금일자)이다. */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { driverApi, driverError, type MyDriverInvoice } from '../lib/driverApi';
import Screen, { Empty, ErrorBanner, HistoryHeading, HistoryTabs, Loading } from './Screen';
import { won } from './format';
import { ProxyTag } from './ProxyTag';

function statusOf(r: MyDriverInvoice): { label: string; cls: string } {
  if (r.cancelled) return { label: '취소신청', cls: 'bg-red-50 text-red-700' };
  if (r.paid_date) return { label: `입금완료 ${r.paid_date}`, cls: 'bg-blue-50 text-blue-700' };
  if (r.issuer) return { label: '발행완료', cls: 'bg-green-50 text-green-700' };
  return { label: '발행 대기', cls: 'bg-canvas text-ink-soft' };
}

export default function MyInvoices() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MyDriverInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    driverApi.listMyInvoices({})
      .then((r) => setRows(r.rows))
      .catch((e) => { setError(driverError(e, '신청 내역을 불러오지 못했습니다.')); setRows([]); });
  }, [nonce]);

  async function cancel(id: string) {
    setBusy(true); setError(null);
    try {
      await driverApi.cancelInvoice({ id: Number(id), reason: reason.trim() || undefined });
      setCancelId(null); setReason(''); setNonce((n) => n + 1);
    } catch (e) { setError(driverError(e, '취소하지 못했습니다.')); } finally { setBusy(false); }
  }

  return (
    <Screen title="세금계산서" acting>
      <HistoryTabs active="invoices" />
      <button className="driver-btn mb-6" onClick={() => navigate('/invoices/new')}>＋ 세금계산서 신청하기</button>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <HistoryHeading />
      {rows === null ? <Loading /> : rows.length === 0 ? <Empty>신청한 세금계산서가 없습니다.</Empty> : (
        <ul className="space-y-4">
          {rows.map((r) => {
            const st = statusOf(r);
            return (
              <li key={r.invoice_id} className="driver-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-ink-soft">{r.invoice_no} · {r.corp_name} · {r.request_date}</p>
                    <p className="mt-1 break-keep text-lg font-bold text-ink">{r.cust_name}</p>
                  </div>
                  <span className={`shrink-0 rounded-lg px-2 py-1 text-sm font-semibold ${st.cls}`}>{st.label}</span>
                </div>
                {r.full && <p className="mt-3 text-lg text-ink">합계 <b>{won(r.total)}</b>원 <span className="text-base text-ink-soft">(공급가 {won(r.supply)} · 부가세 {won(r.vat)})</span></p>}
                {!r.mine && r.my_share && <p className={`${r.full ? 'mt-1 text-base' : 'mt-3 text-lg'} text-brand-700`}>나에게 배분된 금액 <b>{won(r.my_share)}</b>원</p>}
                {r.mine && r.dists.length > 0 && (
                  <ul className="mt-2 space-y-1 text-base text-ink-soft">
                    {r.dists.map((d, i) => <li key={i}>{d.type === '자체배분' ? '내 몫' : d.name} · {won(d.amount)}원</li>)}
                  </ul>
                )}
                {r.issuer && <p className="mt-1 text-base text-ink-soft">발행자 {r.issuer}</p>}
                <ProxyTag by={r.proxy_by} forName={r.proxy_for} />
                {r.mine && !r.cancelled && (
                  cancelId === r.invoice_id ? (
                    <div className="mt-4 space-y-3">
                      <input className="driver-input" placeholder="취소 사유(선택)" value={reason} onChange={(e) => setReason(e.target.value)} />
                      <div className="flex gap-3">
                        <button className="driver-btn bg-red-600 hover:bg-red-700" disabled={busy} onClick={() => void cancel(r.invoice_id)}>
                          취소 신청
                        </button>
                        <button className="min-h-touch w-full rounded-xl border-2 border-line text-lg" onClick={() => setCancelId(null)}>닫기</button>
                      </div>
                      <p className="text-base text-ink-soft">
                        {r.paid_date ? '입금이 확인된 계산서입니다 — 취소를 신청하면 회계팀이 입금을 정리하고 수정 계산서를 발행합니다.'
                          : r.issuer ? '이미 발행된 계산서입니다 — 취소를 신청하면 회계팀이 수정 계산서를 발행합니다.' : '취소를 신청하면 회계팀이 확인한 뒤 처리합니다.'}
                      </p>
                    </div>
                  ) : (
                    <button className="mt-4 min-h-touch w-full rounded-xl border-2 border-line text-lg text-ink-soft" onClick={() => { setCancelId(r.invoice_id); setReason(''); }}>
                      취소하기
                    </button>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Screen>
  );
}
